/**
 * Paginated levels hook for Select components.
 */

import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLevels } from '@/services/levels.service';
import type { SelectOption } from '@/components/forms/Select';

/** Options for {@link useLevelsSelect}. */
interface UseLevelsSelectOptions {
  /** Filter by cycles. */
  cycles?: string[];
  /** Filter by statuses. */
  statuses?: string[];
  /** Free-text search. */
  search?: string;
  /** Min age filter. */
  minAge?: number;
  /** Max age filter. */
  maxAge?: number;
  /** Gate the query. */
  enabled?: boolean;
}

/**
 * Load levels with infinite scroll support for Select components.
 * Accumulates pages as user scrolls.
 */
export const useLevelsSelect = (options?: UseLevelsSelectOptions) => {
  const [page, setPage] = useState(1);
  const [accumulatedData, setAccumulatedData] = useState<SelectOption[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'levels-select',
      options?.cycles,
      options?.statuses,
      options?.search,
      options?.minAge,
      options?.maxAge,
      page,
    ],
    queryFn: () =>
      getLevels({
        page,
        pageSize: 10,
        cycles: options?.cycles,
        statuses: options?.statuses,
        search: options?.search,
        minAge: options?.minAge,
        maxAge: options?.maxAge,
      }),
    enabled: options?.enabled !== false,
  });

  /** Merge new page data into accumulated list. */
  useMemo(() => {
    if (!data?.data) return;
    const newOptions = data.data.map((l) => ({ value: l.id, label: l.label }));

    if (page === 1) {
      setAccumulatedData(newOptions);
    } else {
      setAccumulatedData((prev) => {
        const existingIds = new Set(prev.map((o) => o.value));
        const uniqueNew = newOptions.filter((o) => !existingIds.has(o.value));
        return [...prev, ...uniqueNew];
      });
    }
  }, [data, page]);

  /** Load next page. */
  const onLoadMore = useCallback(() => {
    if (data && page * 10 < data.total) {
      setPage((p) => p + 1);
    }
  }, [data, page]);

  /** Has more pages to load. */
  const hasMore = Boolean(data && page * 10 < data.total);

  return {
    options: accumulatedData,
    isLoading,
    onLoadMore,
    hasMore,
  };
};
